import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import * as api from './api';
import type { UpdateForecastPayload } from './api';
import type {
  ForecastHorizon,
  ForecastMetric,
  ListForecastsParams,
} from './schema';

const ROOT_KEY = 'forecasts';
const listKey = (params: ListForecastsParams) =>
  [ROOT_KEY, 'list', params] as const;
const chartKey = (
  priceSeriesId: string,
  metric: ForecastMetric,
  horizon: ForecastHorizon,
) => [ROOT_KEY, 'chart', priceSeriesId, metric, horizon] as const;

function describeError(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: string | string[]; error?: string }
      | undefined;
    const msg = data?.message;
    if (Array.isArray(msg)) return msg.join(', ');
    if (typeof msg === 'string' && msg.length > 0) return msg;
    if (data?.error) return data.error;
  }
  return fallback;
}

export function useForecasts(params: ListForecastsParams) {
  return useQuery({
    queryKey: listKey(params),
    queryFn: () => api.listForecasts(params),
    placeholderData: (prev) => prev,
  });
}

export function useForecastChart(
  priceSeriesId: string | null,
  metric: ForecastMetric,
  horizon: ForecastHorizon,
) {
  return useQuery({
    queryKey: chartKey(priceSeriesId ?? '', metric, horizon),
    queryFn: () => api.getForecastChart(priceSeriesId as string, metric, horizon),
    enabled: !!priceSeriesId,
    // Forecasts update at most once per pipeline run (typically daily). 5min
    // cache is short enough to catch a fresh run on refresh, long enough to
    // make the metric/horizon toggle feel snappy.
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateForecast() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateForecastPayload;
    }) => api.updateForecast(id, payload),
    onSuccess: () => {
      // Invalidate every cached list params combo. Don't bother with chart
      // invalidation — chart fetches the *latest* row, not by id.
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'list'] });
      toast.success('Forecast updated');
    },
    onError: (err) =>
      toast.error(describeError(err, 'Failed to update forecast')),
  });
}
